/**
 * Sigil triggers — three suggestion plugins keyed on `@`, `!`, `#` that
 * open the directory picker. Selection inserts a plain text token
 * (`@slug `, `!slug `, `#slug `) — purely decorative in body prose; the
 * SlashCapture Enter handler re-parses the line via `parseComposer`, so
 * resolution is automatic when the token sits in a slash line.
 *
 * Ported from Loop's sigilTriggers.ts; pools remapped onto the CRM
 * Directory shape (mirrors grammar.ts): `@` → contacts, `!` →
 * contacts + companies, `#` → targets (companies/leads/events/meetings).
 */
import { Extension } from "@tiptap/core";
import { Suggestion } from "@tiptap/suggestion";
import { PluginKey } from "@tiptap/pm/state";
import { TAG_KIND_LABEL, type Directory, type DirectoryEntry } from "../../../lib/grammar";
import { createSuggestionRenderer } from "./suggestionRenderer";

export interface SigilTriggersOptions {
    getDirectory: () => Directory;
}

export type SigilChar = "@" | "!" | "#";

const PLUGIN_KEYS: Record<SigilChar, PluginKey> = {
    "@": new PluginKey("sigilMention"),
    "!": new PluginKey("sigilAssign"),
    "#": new PluginKey("sigilTag"),
};

const SIGIL_LABEL: Record<SigilChar, string> = {
    "@": "mention contact",
    "!": "assign to",
    "#": "tag account / lead / event / meeting",
};

/** Slug-prefix, alias-prefix, or name-substring match; deduped by id, max 12.
 *  Shared with CaptureBar so both surfaces match identically. */
export function filterDirectory(pool: DirectoryEntry[], query: string): DirectoryEntry[] {
    const q = query.toLowerCase();
    const matched = !q
        ? pool
        : pool.filter((e) =>
            e.slug.toLowerCase().startsWith(q) ||
            e.name.toLowerCase().includes(q) ||
            (e.aliases ?? []).some((a) => a.toLowerCase().startsWith(q)));
    const seen = new Set<string>();
    const out: DirectoryEntry[] = [];
    for (const entry of matched) {
        if (seen.has(entry.id)) continue;
        seen.add(entry.id);
        out.push(entry);
        if (out.length >= 12) break;
    }
    return out;
}

export function sigilPool(sigil: SigilChar, dir: Directory): DirectoryEntry[] {
    return sigil === "#" ? dir.targets
        : sigil === "!" ? [...dir.contacts, ...dir.companies]
        : dir.contacts;
}

function buildSuggestion(
    sigil: SigilChar,
    options: SigilTriggersOptions,
    editor: import("@tiptap/core").Editor,
) {
    return Suggestion<DirectoryEntry, DirectoryEntry>({
        editor,
        pluginKey: PLUGIN_KEYS[sigil],
        char: sigil,
        startOfLine: false,
        allowSpaces: false,
        // Suppress mid-token — only open when the cursor sits at the end
        // of the typed query (next char is whitespace/end), so clicking
        // into "@greg|-montgomery" doesn't pop it. `#` at line start is
        // allowed: a tag there is far more common than an H1, and "# "
        // still makes a heading (the space closes the menu first).
        // try/catch: transient mid-edit doc shapes must not crash the
        // editor; worst case the popover briefly reappears.
        allow: ({ state, range }) => {
            try {
                const docSize = state.doc.content.size;
                const to = Math.min(Math.max(range.to, 0), docSize);
                const nextEnd = Math.min(to + 1, docSize);
                if (nextEnd > to) {
                    const nextChar = state.doc.textBetween(to, nextEnd, "\n", "\n");
                    if (nextChar && /\S/.test(nextChar)) return false;
                }
                return true;
            } catch {
                return true;
            }
        },
        items: ({ query }) => {
            return filterDirectory(sigilPool(sigil, options.getDirectory()), query);
        },
        command: ({ editor, range, props }) => {
            // Plain text token — parser-visible, styling-free.
            editor.chain().focus().deleteRange(range).insertContent(`${sigil}${props.slug} `).run();
        },
        render: () =>
            createSuggestionRenderer<DirectoryEntry>({
                label: SIGIL_LABEL[sigil],
                toItem: (entry) => ({
                    key: entry.id,
                    label: entry.name,
                    hint: entry.kind === "contact" ? null : TAG_KIND_LABEL[entry.kind],
                    kind: entry.kind,
                }),
            }),
    });
}

export const SigilTriggers = Extension.create<SigilTriggersOptions>({
    name: "sigilTriggers",

    addOptions() {
        return {
            getDirectory: () => ({ contacts: [], companies: [], targets: [] }),
        };
    },

    addProseMirrorPlugins() {
        return [
            buildSuggestion("@", this.options, this.editor),
            buildSuggestion("!", this.options, this.editor),
            buildSuggestion("#", this.options, this.editor),
        ];
    },
});
